import React, { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import './PaymentAlertBanner.css';

const DISMISS_KEY = 'volcyy:paymentAlertDismissed';

// Shown on every page while a renewal is being retried.
//
// Item 3 is about a member opening the app, finding their Black discounts gone
// and concluding they were cut off. The discounts no longer disappear — but a
// member who is never told still learns about the failed payment whenever the
// retries finally run out, which is the same surprise moved later. The email
// is the push half of that; this is the half that reaches someone who opens the
// app without reading their mail.
//
// Deliberately app-wide rather than on the profile page: the homepage is where
// people land, and a warning only visible on the page nobody visits is not a
// warning.
export default function PaymentAlertBanner() {
  const { needsPaymentUpdate } = useAuth();
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState('');
  // Session-scoped, not permanent: dismissing quiets it for this visit, and it
  // returns next time. The problem does not go away when the banner does.
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  });

  if (!needsPaymentUpdate || dismissed) return null;

  function dismiss() {
    try {
      sessionStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* private mode — the banner simply stays for this page view */
    }
    setDismissed(true);
  }

  async function openPortal() {
    setOpening(true);
    setError('');
    try {
      const createBillingPortalSession = httpsCallable(functions, 'createBillingPortalSession');
      const res = await createBillingPortalSession({ origin: window.location.origin });
      if (res.data?.url) {
        window.location.href = res.data.url;
        return;
      }
      setError("We couldn't open your billing settings. Please try again.");
    } catch (e) {
      setError(e.message || "We couldn't open your billing settings. Please try again.");
    } finally {
      setOpening(false);
    }
  }

  return (
    <div className="payment-alert" role="status">
      <div className="payment-alert__inner">
        <p className="payment-alert__text">
          <strong>Your card was declined.</strong>{' '}
          Your Black discounts are still active — update your payment method to keep them.
          {error && <span className="payment-alert__error"> {error}</span>}
        </p>
        <div className="payment-alert__actions">
          <button
            type="button"
            className="payment-alert__button"
            onClick={openPortal}
            disabled={opening}
          >
            {opening ? 'Opening…' : 'Update card'}
          </button>
          <button
            type="button"
            className="payment-alert__dismiss"
            onClick={dismiss}
            aria-label="Dismiss for this visit"
          >
            ×
          </button>
        </div>
      </div>
    </div>
  );
}
